import { useEffect, useState } from "react";
import {
  ClipboardIcon,
  CheckIcon,
  ArrowLeftIcon,
} from "@heroicons/react/24/outline";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../contexts/AuthContext";
import AdminCompanyEdit from "./AdminCompanyEdit";
import { formatPhoneNumber } from "../../../globalFunctions/PhoneNumberFormatter";
import { useNavigate } from "react-router-dom";

export default function AdminCompanyTable() {
  const { t } = useTranslation();
  const { currentUser } = useAuth();
  const userId = currentUser?.id;
  const [companies, setCompanies] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [copiedId, setCopiedId] = useState(null);
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    document.body.style.overflow = isOpenModal ? "hidden" : "auto";
    return () => (document.body.style.overflow = "auto");
  }, [isOpenModal]);

  useEffect(() => {
    const fetchCompanies = async () => {
      setLoading(true);
      try {
        const res = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/Company/Admin/All`,
          {
            headers: {
              "Content-Type": "application/json",
              "X-User-Id": userId,
            },
          }
        );
        const data = await res.json();
        setCompanies(data);
      } catch (err) {
        console.error(err);
        setError("Erreur de chargement.");
      } finally {
        setLoading(false);
      }
    };

    fetchCompanies();
  }, []);

  const handleCopy = (slug) => {
    const fullUrl = `${window.location.origin}/compagnie/${slug}`;
    navigator.clipboard.writeText(fullUrl);
    setCopiedId(slug);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleEdit = (company) => {
    setSelectedCompany(company);
    setIsOpenModal(true);
  };

  const filteredCompanies = companies.filter((c) =>
    c.name?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div>
      <button
        onClick={() => navigate(-1)}
        className="btn bg-primary text-white rounded-full flex items-center gap-2 m-4 mb-0"
      >
        <ArrowLeftIcon className="w-5 h-5" />
        {t("global.back")}
      </button>
      <div className="p-6 space-y-6 text-primary">
        <div className="flex justify-between items-center gap-4 flex-wrap">
          <h1 className="text-2xl font-bold">Gestion des compagnies</h1>
          <input
            type="text"
            placeholder="Rechercher une compagnie..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="input input-bordered w-full md:w-64"
          />
        </div>

        <div className="overflow-x-auto rounded-box border border-base-content/10 bg-base-100">
          <table className="table table-zebra">
            <thead>
              <tr>
                <th>Logo</th>
                <th>Nom</th>
                <th>Actif</th>

                <th>Contact</th>
                <th>Téléphone</th>
                <th>Lien public (BETA)</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="7" className="text-center">
                    Chargement...
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan="7" className="text-red-500 text-center">
                    {error}
                  </td>
                </tr>
              ) : filteredCompanies.length === 0 ? (
                <tr>
                  <td colSpan="7" className="text-center text-gray-500">
                    Aucune compagnie trouvée.
                  </td>
                </tr>
              ) : (
                filteredCompanies.map((c) => (
                  <tr key={c.id}>
                    <td>
                      <img
                        src={c.imageUrl || "/assets/notfound.png"}
                        alt={c.name}
                        className="w-12 h-12 rounded-full object-cover"
                      />
                    </td>
                    <td>{c.name}</td>
                    <td>
                      <span
                        className={`badge ${
                          c.isActive ? "badge-success" : "badge-error"
                        }`}
                      >
                        {c.isActive ? "Oui" : "Non"}
                      </span>
                    </td>

                    <td>{c.contactPerson || "-"}</td>
                    <td>{formatPhoneNumber(c.phoneNumber) || "-"}</td>
                    <td>
                      <div className="flex items-center gap-2">
                        <button
                          className="btn btn-sm btn-ghost"
                          onClick={() => handleCopy(c.slug)}
                        >
                          <AnimatePresence mode="wait" initial={false}>
                            {copiedId === c.slug ? (
                              <motion.span
                                key="check"
                                initial={{ opacity: 0, scale: 0.8 }}
                                animate={{ opacity: 1, scale: 1 }}
                                exit={{ opacity: 0, scale: 0.8 }}
                              >
                                <CheckIcon className="w-5 h-5 text-green-500" />
                              </motion.span>
                            ) : (
                              <motion.span
                                key="clipboard"
                                initial={{ opacity: 0, scale: 0.8 }}
                                animate={{ opacity: 1, scale: 1 }}
                                exit={{ opacity: 0, scale: 2 }}
                              >
                                <ClipboardIcon className="w-5 h-5 text-gray-700" />
                              </motion.span>
                            )}
                          </AnimatePresence>
                        </button>
                      </div>
                    </td>
                    <td>
                      <button
                        onClick={() => handleEdit(c)}
                        className="bg-primary p-2 btn-sm rounded-full text-white"
                      >
                        Modifier
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <AdminCompanyEdit
        key={selectedCompany?.id || "new"}
        isOpen={isOpenModal}
        onClose={() => {
          setIsOpenModal(false);
          setSelectedCompany(null);
        }}
        company={selectedCompany}
        onSave={(updatedCompany) => {
          setCompanies((prev) => {
            const exists = prev.some((c) => c.id === updatedCompany.id);
            return exists
              ? prev.map((c) =>
                  c.id === updatedCompany.id ? updatedCompany : c
                )
              : [...prev, updatedCompany];
          });
          setIsOpenModal(false);
          setSelectedCompany(null);
        }}
      />
    </div>
  );
}
